import { useState, useEffect, useRef, useMemo } from 'react';
import { thumbnailShell } from '../../../styles/thumbnailShell';
import { mapChromeShell } from '../../../styles/mapChromeShell';

export default function ThumbnailOnViewer({ url, alt = 'thumbnail', markerId }) {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isInView, setIsInView] = useState(false);
    const imgRef = useRef(null);
    const observerRef = useRef(null);
    const prevUrlRef = useRef(null);

    useEffect(() => {
        if (!imgRef.current) return;

        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        setIsInView(true);
                        observerRef.current?.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.1, rootMargin: '50px' }
        );

        observerRef.current.observe(imgRef.current);

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, []);

    useEffect(() => {
        if (url !== prevUrlRef.current) {
            setIsLoaded(false);
            prevUrlRef.current = url;
        }
    }, [url]);

    const handleImageLoad = () => {
        setIsLoaded(true);
    };

    const handleImageError = () => {
        console.warn('Thumbnail failed to load:', url);
        setIsLoaded(true);
    };

    const imageStyle = useMemo(() => ({
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        opacity: isLoaded ? 1 : 0,
        transition: 'opacity 0.2s ease-in-out',
        imageOrientation: 'from-image'
    }), [isLoaded]);

    return (
        <div ref={imgRef} className={`${thumbnailShell.viewer} items-center justify-center`}>
            {!isLoaded && (
                <div className={mapChromeShell.loading}>読み込み中...</div>
            )}
            {isInView && url && (
                <img
                    src={url}
                    alt={alt}
                    style={imageStyle}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    loading="eager"
                    decoding="async"
                    className={thumbnailShell.viewerImage}
                    key={`${markerId}-${url}`}
                />
            )}
        </div>
    );
}
